import { NavLink, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useLangPath } from "@/components/useLangPath";

const navItems = [
  { to: "/solutions", key: "solutions" },
  { to: "/how-it-works", key: "how_it_works" },
  { to: "/technology", key: "technology" },
  { to: "/governance", key: "governance" },
  { to: "/resources", key: "resources" },
  { to: "/case-studies", key: "case_studies" },
  { to: "/media", key: "media" },
  { to: "/awards", key: "awards" },
  { to: "/contact", key: "contact" },
];

export default function Navbar() {
  const { t } = useTranslation();
  const withLang = useLangPath();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <nav
      data-testid="top-navbar"
      className={`fixed top-0 w-full z-50 border-b border-zinc-200/50 bg-cream/90 backdrop-blur-xl transition-shadow ${
        scrolled ? "shadow-md" : "shadow-sm"
      }`}
    >
      <div className="max-w-[1280px] mx-auto flex items-center justify-between px-6 h-16">
        <Link to={withLang("/")} data-testid="nav-logo" className="flex items-center h-full py-1.5 group">
          <img
            src="/logos/aocems.svg"
            alt="AOCEMS"
            className="h-full w-auto object-contain"
          />
          <span className="sr-only">AOCEMS</span>
        </Link>

        <div className="hidden lg:flex items-center gap-6 font-manrope text-sm font-medium tracking-tight">
          {navItems.map((l) => (
            <NavLink
              key={l.to}
              to={withLang(l.to)}
              data-testid={`nav-link-${l.to.replace("/", "")}`}
              className={({ isActive }) =>
                isActive
                  ? "text-brand font-bold border-b-2 border-brand pb-1"
                  : "text-zinc-700 hover:text-brand transition-colors"
              }
            >
              {t(`nav.${l.key}`)}
            </NavLink>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <LanguageSwitcher />
          <Link
            to={withLang("/contact")}
            data-testid="nav-cta-demo"
            className="hidden sm:inline-block px-5 py-2 bg-brand text-white font-semibold rounded-lg text-sm hover:opacity-90 active:scale-95 duration-200 transition-all"
          >
            {t("nav.request_demo")}
          </Link>
          <button
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-menu-toggle"
            className="lg:hidden text-zinc-700 p-2 rounded-md hover:bg-zinc-100"
            aria-label="Toggle menu"
          >
            <span className="material-symbols-outlined">{open ? "close" : "menu"}</span>
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-zinc-200/60 bg-cream/95 backdrop-blur-xl">
          <div className="max-w-[1280px] mx-auto px-6 py-4 flex flex-col gap-2">
            {navItems.map((l) => (
              <NavLink
                key={l.to}
                to={withLang(l.to)}
                onClick={() => setOpen(false)}
                data-testid={`nav-mobile-link-${l.to.replace("/", "")}`}
                className={({ isActive }) =>
                  `py-2 px-3 rounded-lg font-manrope text-sm ${
                    isActive ? "bg-brand text-white" : "text-zinc-700 hover:bg-zinc-100"
                  }`
                }
              >
                {t(`nav.${l.key}`)}
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
}
